import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { GoogleOneTap } from '@sorare/core/src/components/GoogleOneTap';
import { ReportCampaignVisit } from '@sorare/core/src/components/ReportCampaignVisit';
import { ResolveImpactClickId } from '@sorare/core/src/components/ResolveImpactClickId';
import { JonumStatusDialogs } from '@sorare/core/src/components/jonum/JonumStatusDialogs';
import { MangopayConsentDialog } from '@sorare/core/src/components/mangopayConsent/MangopayConsentDialog';
import { BlockTwaLongPressMenu } from '@sorare/core/src/components/mobileApp/BlockTwaLongPressMenu';
import { MobileAppHelmet } from '@sorare/core/src/components/mobileApp/MobileAppHelmet';
import { RegisterTwaDeviceToken } from '@sorare/core/src/components/mobileApp/RegisterTwaDeviceToken';
import { NetworkInfo } from '@sorare/core/src/components/navigation/NetworkInfo';
import { RestrictedAccessModals } from '@sorare/core/src/components/restrictedAccess/RestrictedAccessModals';
import { CookieConsentBanner } from '@sorare/core/src/components/user/CookieConsentBanner';
import { Eip7702RevocationDialog } from '@sorare/core/src/components/wallet/Eip7702Revocation';
import { WalletSetup } from '@sorare/core/src/components/wallet/WalletSetup';
import { isMockprod, isStaging } from '@sorare/core/src/config';
import { AccountSecurityCheckProvider } from '@sorare/core/src/contexts/accountSecurityCheck/Provider';
import { AuthProvider } from '@sorare/core/src/contexts/auth/Provider';
import { ConfigProvider } from '@sorare/core/src/contexts/config/Provider';
import { ConnectionProvider } from '@sorare/core/src/contexts/connection/Provider';
import { CurrentUserProvider } from '@sorare/core/src/contexts/currentUser/Provider';
import { DialogOpenedProvider } from '@sorare/core/src/contexts/dialogOpened/Provider';
import { EventsProvider } from '@sorare/core/src/contexts/events/Provider';
import { StreamWhenSignedIn } from '@sorare/core/src/contexts/featureFlags/StreamWhenSignedIn';
import { FlyTargetProvider } from '@sorare/core/src/contexts/flyTarget';
import { HighlightProvider } from '@sorare/core/src/contexts/highlight/Provider';
import { KycRenewalRequiredProvider } from '@sorare/core/src/contexts/kycRenewalRequired/Provider';
import { MangopayConsentProvider } from '@sorare/core/src/contexts/mangopayConsent/Provider';
import { MousePositionProvider } from '@sorare/core/src/contexts/mousePosition';
import { OnboardingHighlightProvider } from '@sorare/core/src/contexts/onboardingHighlight/Provider';
import { OneTimeDialogProvider } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import { RestrictedAccessProvider } from '@sorare/core/src/contexts/restrictedAccess/Provider';
import { SeoProvider } from '@sorare/core/src/contexts/seo/Provider';
import { SnackNotification } from '@sorare/core/src/contexts/snackNotification/Provider';
import { SportProvider } from '@sorare/core/src/contexts/sport/Provider';
import { TickerProvider } from '@sorare/core/src/contexts/ticker/Provider';
import { FrameWhenNeeded as WalletFrame } from '@sorare/core/src/contexts/wallet/FrameWhenNeeded';
import { WalletProvider } from '@sorare/core/src/contexts/wallet/Provider';
import { WalletDrawerProvider } from '@sorare/core/src/contexts/walletDrawer/Provider';
import { useSyncAuthStateAcrossTabs } from '@sorare/core/src/hooks/auth/useSyncAuthStateAcrossTabs';
import { useMeasureScrollbars } from '@sorare/core/src/hooks/useMeasureScrollbars';
import { useStyledScrollbar } from '@sorare/core/src/hooks/useStyledScrollbar';
import { useVh } from '@sorare/core/src/hooks/useVh';
import { logVersion } from '@sorare/core/src/lib/logVersion';
import { lazy } from '@sorare/core/src/lib/retry';
import { EnsureLatestAppVersion } from '@sorare/core/src/routing/EnsureLatestAppVersion';
import { RouteMeta } from '@sorare/core/src/routing/RouteMeta';
import { MarketplaceProvider } from '@sorare/marketplace/src/contexts/Marketplace/Provider';
import { SingleSaleOfferProvider } from '@sorare/marketplace/src/contexts/singleSaleOffer/Provider';
import { DialogsProvider } from '@sorare/gameplay/src/contexts/dialogs';

const DebugMenu = lazy(
  async () => import('@sorare/core/src/components/DebugMenu'),
  'DebugMenu'
);

const LocationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page(undefined);
    }
  }, [location.pathname]);

  return null;
};

export const AppProviders = ({ children }: { children: ReactNode }) => {
  useVh();
  useMeasureScrollbars();
  useStyledScrollbar();
  useSyncAuthStateAcrossTabs();
  useEffect(() => {
    logVersion();
  }, []);

  return (
    <>
      <LocationTracker />
      <MobileAppHelmet />
      <BlockTwaLongPressMenu />
      <EnsureLatestAppVersion />
      <ReportCampaignVisit />
      <SnackNotification />
      <EventsProvider>
        <DialogOpenedProvider>
          <FlyTargetProvider>
            <HighlightProvider>
              <OnboardingHighlightProvider>
                <SportProvider>
                  <Suspense fallback={<LoadingIndicator fullScreen />}>
                    <ConfigProvider>
                      <SeoProvider>
                        <RouteMeta />
                        <MangopayConsentProvider>
                          <RestrictedAccessProvider>
                            <CurrentUserProvider>
                              <KycRenewalRequiredProvider>
                                <ResolveImpactClickId />
                                <RegisterTwaDeviceToken />
                                <StreamWhenSignedIn />
                                <AuthProvider>
                                  <WalletDrawerProvider>
                                    <WalletProvider>
                                      <ConnectionProvider>
                                        <GoogleOneTap />
                                        <AccountSecurityCheckProvider>
                                          <WalletSetup />
                                          <SingleSaleOfferProvider>
                                            <TickerProvider>
                                              <NetworkInfo />
                                              <RestrictedAccessModals />
                                              <MangopayConsentDialog />
                                              <Eip7702RevocationDialog />
                                              <OneTimeDialogProvider>
                                                <JonumStatusDialogs />
                                                <MarketplaceProvider>
                                                  <DialogsProvider>
                                                    <MousePositionProvider>
                                                      <Suspense fallback={null}>
                                                        {children}
                                                      </Suspense>
                                                      {!!(
                                                        import.meta.env.DEV ||
                                                        isStaging ||
                                                        isMockprod
                                                      ) && (
                                                        <Suspense
                                                          fallback={null}
                                                        >
                                                          <DebugMenu />
                                                        </Suspense>
                                                      )}
                                                      <Suspense fallback={null}>
                                                        <WalletFrame />
                                                      </Suspense>
                                                    </MousePositionProvider>
                                                  </DialogsProvider>
                                                </MarketplaceProvider>
                                              </OneTimeDialogProvider>
                                            </TickerProvider>
                                          </SingleSaleOfferProvider>
                                        </AccountSecurityCheckProvider>
                                      </ConnectionProvider>
                                    </WalletProvider>
                                  </WalletDrawerProvider>
                                </AuthProvider>
                              </KycRenewalRequiredProvider>
                            </CurrentUserProvider>
                          </RestrictedAccessProvider>
                        </MangopayConsentProvider>
                      </SeoProvider>
                    </ConfigProvider>
                  </Suspense>
                </SportProvider>
              </OnboardingHighlightProvider>
            </HighlightProvider>
          </FlyTargetProvider>
        </DialogOpenedProvider>
        <CookieConsentBanner />
      </EventsProvider>
    </>
  );
};
